import TablePagination from 'components/table/TablePagination';
import {
   Table as FbTable,
   TableBody as FbTableBody,
   TableCell as FbTableCell,
   TableHead as FbTableHead,
   TableHeadCell as FbTableHeadCell,
   Spinner,
   TableRowTheme,
   TableTheme,
} from 'flowbite-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiOutlineChevronDown, HiOutlineChevronUp } from 'react-icons/hi';
import { twMerge } from 'tailwind-merge';
import { CSS } from '@dnd-kit/utilities';
import { DndContext, closestCenter, DragEndEvent, UniqueIdentifier } from '@dnd-kit/core';
import { useSortable, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';

const defaultTheme = {
   root: {
      wrapper: 'w-full h-full flex-1',
   },
   head: {
      base: 'bg-white border-b-2 group/head',
      cell: {
         base: 'px-4 py-1.5 bg-white text-black text-xs normal-case',
      },
   },
   body: {
      base: 'group/body bg-white',
      cell: {
         base: 'px-4 py-1.5',
      },
   },
   row: {
      base: 'w-full group/row cursor-pointer py-1.5',
      hovered: 'hover:bg-bgNavInteract',
   },
};

export type TableColDef<T> = {
   align?: 'left' | 'right' | 'center';
   field: string;
   flex?: number;
   headerAlign?: 'left' | 'right' | 'center';
   headerName?: React.ReactNode;
   minWidth?: number;
   maxWidth?: number;
   sortable?: boolean;
   columnClassName?: string;
   render?: (row: T) => React.ReactNode;
};

type SortOrder = 'ASC' | 'DESC' | 'NONE';

type SortModel = {
   field: string;
   order: SortOrder;
};

type TableProps<T> = {
   columns: TableColDef<T>[];
   currentPage?: number;
   isLoading?: boolean;
   pagination?: boolean;
   rows?: T[];
   draggable?: boolean;
   totalPage?: number;
   onPageChanged?: (page: number) => void;
   onRowSelect?: (row: T) => void;
   onSort?: (models: SortModel[]) => void;
   onDrop?: (items: T) => void;
   theme?: Partial<TableTheme>;
   className?: string;
   /**
    * Stable identity for a row. Rows used to be keyed with crypto.randomUUID(),
    * which hands React a brand-new key on EVERY render: every row unmounts and
    * remounts on any state change, so focus inside a row (a checkbox, an input)
    * is thrown back to <body> and keystroke-driven filtering re-creates the
    * whole body. Defaults to row.id, then the row index.
    */
   rowKey?: (row: T, index: number) => string | number;
   /** Replaces the default `common.emptyData` message (pass '' to render nothing). */
   emptyText?: string;
   /** Seeds the header arrow when a sort is restored from the URL. */
   initialSortModel?: SortModel;
   /** Exposes the scrolling body so a page can save and restore its offset. */
   bodyRef?: React.Ref<HTMLDivElement>;
   /** Accessible name for the table (WCAG 1.3.1): announced when a screen reader enters it. */
   ariaLabel?: string;
};

const TableCellWrapper = ({ children }: { children: React.ReactNode }) => {
   return <div className={twMerge(`flex w-full h-full min-w-0 overflow-hidden`)}>{children}</div>;
};

// For drag and drop
const SortableRow = React.memo(
   ({ id, children, gridTemplateColumns }: { id: string | number; children: React.ReactNode; gridTemplateColumns: string }) => {
      const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

      const style = {
         transform: CSS.Transform.toString(transform),
         transition,
         cursor: 'grab',
         height: '54px',
         borderRadius: '6px',
         marginTop: '10px',
         display: 'grid',
         gap: '10px',
         border: '1px solid #E8E8E8',
         borderColor: isDragging ? '#62A4AB' : '#E8E8E8',
         backgroundColor: isDragging ? '#F7F7F7' : 'white',
         opacity: isDragging ? 0.8 : 1,
         boxShadow: isDragging ? '0px 0px 4px 0px #00000099' : 'none',
         gridTemplateColumns,
      } as React.CSSProperties;

      return (
         <tr ref={setNodeRef} style={style} {...attributes} {...listeners}>
            {children}
         </tr>
      );
   },
);

const Table: React.FC<TableProps<any>> = ({
   columns,
   currentPage,
   isLoading,
   pagination = true,
   rows = [],
   totalPage,
   onRowSelect,
   onSort,
   onPageChanged,
   draggable = false,
   onDrop,
   theme,
   className,
   rowKey,
   emptyText,
   initialSortModel,
   bodyRef,
   ariaLabel,
}) => {
   const [t] = useTranslation();
   const [reorderRows, setReorderRows] = useState(rows);

   const gridTemplateColumns = useMemo(
      () =>
         columns
            .map(col => {
               const min = col.minWidth ?? 0;

               if (col.maxWidth != null) {
                  return `minmax(${min}px, ${col.maxWidth}px)`;
               }

               const flex = col.flex ?? 0;
               return `minmax(${min}px, ${flex}fr)`;
            })
            .join(' '),
      [columns],
   );

   useEffect(() => {
      setReorderRows(rows);
   }, [rows]);

   // **  For drag and drop ** //
   const reorderWithIndex = ({ items, activeId, overId }: { items: any; activeId: UniqueIdentifier; overId: UniqueIdentifier }) => {
      const list = [...items];
      const from = list.findIndex(i => i.id === activeId);
      const to = list.findIndex(i => i.id === overId);
      if (from === -1 || to === -1) return list;

      const [moved] = list.splice(from, 1);
      list.splice(to, 0, moved);

      list.forEach((item, idx) => (item.index = idx + 1));

      return list;
   };

   const handleDragEnd = (event: DragEndEvent) => {
      const { active, over } = event;

      // Drop and drag to itselft point
      if (!over || active.id === over.id) return;

      // Reorder list
      const updatedList = reorderWithIndex({ items: reorderRows, activeId: active.id, overId: over.id });

      // Update UI immediatly
      setReorderRows(updatedList);

      // Callback
      onDrop?.(updatedList.map(item => ({ id: item.id, index: item.index })));
   };
   // ************************ //

   const [sortModels, setSortModels] = useState<SortModel[]>(initialSortModel ? [initialSortModel] : []);

   const renderHeadCells = () => {
      const headStyle: React.CSSProperties = { display: 'grid', gridTemplateColumns, gap: draggable ? '10px' : '0' };

      return (
         <tr style={headStyle}>
            {columns?.map(config => {
               const sortModel = sortModels?.filter(model => model.field === config.field)?.[0];

               return (
                  <FbTableHeadCell
                     scope="col"
                     key={config.field}
                     className={`text-${config.headerAlign || 'left'}`}
                     style={{ minWidth: config.minWidth ? `${config.minWidth}px` : undefined, maxWidth: config.maxWidth ? `${config.maxWidth}px` : undefined }}
                  >
                     <TableCellWrapper>
                        <div
                           className={`flex items-center w-fit ${config.columnClassName} ${
                              config.headerAlign === 'center' ? 'm-auto' : config.headerAlign === 'right' ? 'ml-auto' : ''
                           } ${config.sortable === true ? 'cursor-pointer' : ''}`}
                           onClick={
                              config.sortable
                                 ? () => {
                                      const isSameField = sortModel?.field === config.field;

                                      const orders: SortOrder[] = ['ASC', 'DESC', 'NONE'];
                                      const nextOrder = isSameField ? orders[(orders.indexOf(sortModel?.order) + 1) % orders.length] : 'ASC';

                                      if (nextOrder === 'NONE') {
                                         setSortModels([]);
                                         onSort?.([]);
                                      }

                                      const newModel = [{ field: config.field, order: nextOrder }];
                                      setSortModels(newModel);
                                      onSort?.(newModel);
                                   }
                                 : undefined
                           }
                        >
                           <span>{config.headerName || ''}</span>
                           {config?.sortable === true &&
                              (sortModel?.order === 'ASC' ? (
                                 <span className="ml-1">
                                    <HiOutlineChevronUp color="black" size={12} />
                                 </span>
                              ) : sortModel?.order === 'DESC' ? (
                                 <span className="ml-1">
                                    <HiOutlineChevronDown color="black" size={12} />
                                 </span>
                              ) : undefined)}
                        </div>
                     </TableCellWrapper>
                  </FbTableHeadCell>
               );
            })}
         </tr>
      );
   };

   const renderCells = (row: Record<string, any>) => {
      return columns?.map(config => {
         const value = row[config.field];

         let children: React.ReactNode | string = config?.render ? config?.render(row) : <div>{value || ''}</div>;
         if (React.isValidElement(children)) {
            if (config.align === 'right') {
               children = React.cloneElement<React.HTMLAttributes<HTMLElement>>(children as any, {
                  className: twMerge((children as any).props.className, 'w-fit ml-auto'),
               });
            } else if (config.align === 'center') {
               children = React.cloneElement<React.HTMLAttributes<HTMLElement>>(children as any, {
                  className: twMerge((children as any).props.className, 'w-fit m-auto'),
               });
            }
         }

         return (
            <FbTableCell
               key={config.field}
               className={`text-${config.align || 'left'}`}
               style={{ minWidth: config.minWidth ? `${config.minWidth}px` : undefined, maxWidth: config.maxWidth ? `${config.maxWidth}px` : undefined }}
            >
               <TableCellWrapper>{children}</TableCellWrapper>
            </FbTableCell>
         );
      });
   };

   const renderRows = (themeRow?: TableRowTheme) => {
      if (!reorderRows?.length && !isLoading) {
         const message = emptyText ?? t('common.emptyData');
         if (!message) return null;
         return (
            <tr className="border-b border-gray-300">
               <FbTableCell colSpan={columns.length} className="bg-lightGray !rounded-none">
                  <div className="flex items-center justify-center w-full text-center font-bold text-lg h-[78px]">{message}</div>
               </FbTableCell>
            </tr>
         );
      }

      if (draggable) {
         return (
            <>
               <SortableContext items={reorderRows.map(i => i.id)} strategy={verticalListSortingStrategy}>
                  {reorderRows.map(row => (
                     <SortableRow key={row.id} id={row.id} gridTemplateColumns={gridTemplateColumns}>
                        {renderCells(row)}
                     </SortableRow>
                  ))}
               </SortableContext>
            </>
         );
      }

      return rows.map((row, index) => (
         <tr
            className={twMerge('border-b border-gray-300', themeRow?.base, themeRow?.hovered, themeRow?.striped)}
            style={{ display: 'grid', gridTemplateColumns }}
            key={rowKey ? rowKey(row, index) : (row?.id ?? index)}
            onClick={
               onRowSelect
                  ? e => {
                       e.preventDefault();
                       e.stopPropagation();

                       onRowSelect?.(row);
                    }
                  : undefined
            }
         >
            {renderCells(row)}
         </tr>
      ));
   };

   return (
      <div className={className ?? 'flex flex-col w-full h-full gap-7.5'}>
         {/* Table content — overflow only on table body */}
         <div ref={bodyRef} className="flex-1 overflow-auto">
            {draggable ? (
               <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <FbTable hoverable aria-label={ariaLabel} theme={{ ...defaultTheme, ...theme }}>
                     <FbTableHead>{renderHeadCells()}</FbTableHead>
                     <FbTableBody className="mt-3 md:text-center">
                        {isLoading ? (
                           <tr>
                              <FbTableCell colSpan={columns.length} className="bg-white">
                                 <div className="flex items-center justify-center w-full py-5">
                                    <Spinner color="gray" className="text-gray-400" />
                                 </div>
                              </FbTableCell>
                           </tr>
                        ) : (
                           renderRows(theme?.row)
                        )}
                     </FbTableBody>
                  </FbTable>
               </DndContext>
            ) : (
               <FbTable hoverable aria-label={ariaLabel} theme={{ ...defaultTheme, ...theme }}>
                  <FbTableHead>{renderHeadCells()}</FbTableHead>
                  <FbTableBody className="mt-3 md:text-center">
                     {isLoading ? (
                        <tr>
                           <FbTableCell colSpan={columns.length} className="bg-white">
                              <div className="flex items-center justify-center w-full py-5">
                                 <Spinner color="gray" className="text-gray-400" />
                              </div>
                           </FbTableCell>
                        </tr>
                     ) : (
                        renderRows(theme?.row)
                     )}
                  </FbTableBody>
               </FbTable>
            )}
         </div>

         {/* Pagination — always at bottom, never overlapped */}
         {pagination && (
            <TablePagination
               currentPage={currentPage || 0}
               disabled={rows.length === 0}
               total={totalPage || 1}
               onChange={(page: number) => {
                  onPageChanged?.(page - 1);
               }}
            />
         )}
      </div>
   );
};

export type { SortModel, SortOrder };
export default Table;
